import { Select as SelectPrimitive } from 'radix-ui';
import {
  CSSProperties,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type ReactNode,
  type SVGProps,
  forwardRef,
} from 'react';

import {
  ArrowDown,
  ArrowDownContainer,
  ArrowDownIcon,
  ArrowUp,
  ArrowUpContainer,
  CustomItem,
  CustomViewport,
  IconContainer,
  IndicatorContainer,
  SelectContent,
  SelectIndicatorIcon,
  SelectTrigger,
  SelectTriggerContent,
} from './styles';

export interface IBaseSelectProps
  extends
    Omit<SelectPrimitive.SelectProps, 'value'>,
    Pick<HTMLAttributes<HTMLButtonElement>, 'className' | 'style'> {
  children: ReactNode;
  customContentStyle?: CSSProperties;
  dataCy?: string;
  defaultValue?: string;
  id?: string;
  noArrow?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /**
   * @deprecated
   * Please use `onKeyDown` instead.
   */
  onKeyPress?: KeyboardEventHandler<HTMLDivElement>;
  overwritePortalHeight?: number;
  placeholder?: string;
  icon?: ReactNode;
  themeStyle?: 'admin' | 'default';
  zIndex?: number;
  value?: string | null;
  side?: SelectPrimitive.SelectContentProps['side'];
  sideOffset?: SelectPrimitive.SelectContentProps['sideOffset'];
  avoidCollisions?: SelectPrimitive.SelectContentProps['avoidCollisions'];
  align?: SelectPrimitive.SelectContentProps['align'];
  position?: SelectPrimitive.SelectContentProps['position'];
  sticky?: SelectPrimitive.SelectContentProps['sticky'];
  collisionPadding?: SelectPrimitive.SelectContentProps['collisionPadding'];
  valueDisplayText?: string | null;
}

export const BaseSelect = ({
  children,
  className,
  customContentStyle,
  dataCy = 'select-field',
  noArrow = false,
  onKeyDown,
  onKeyPress,
  overwritePortalHeight,
  placeholder = 'Select…',
  style,
  themeStyle = 'default',
  zIndex,
  icon,
  value,
  side = 'bottom',
  sideOffset = 5,
  avoidCollisions = true,
  align = 'start',
  position = 'popper',
  sticky = 'partial',
  collisionPadding = 0,
  valueDisplayText = null,
  ...props
}: IBaseSelectProps): JSX.Element => {
  const isValueProvided = value !== undefined;
  const selectValue = value === null ? '' : value;

  const triggerValueContent = !isValueProvided ? (
    valueDisplayText != null ? (
      valueDisplayText
    ) : undefined
  ) : selectValue === '' ? (
    <p>{placeholder}</p>
  ) : valueDisplayText != null ? (
    valueDisplayText
  ) : undefined;

  return (
    <SelectPrimitive.Root
      {...props}
      {...(isValueProvided ? { value: selectValue ?? '' } : {})}
    >
      <SelectTrigger
        data-cy={dataCy}
        $themeStyle={themeStyle}
        style={style}
        className={className}
      >
        <SelectTriggerContent className="select-trigger-content">
          {icon}
          <SelectPrimitive.Value
            data-cy={placeholder.toLowerCase()}
            placeholder={<p>{placeholder}</p>}
          >
            {triggerValueContent}
          </SelectPrimitive.Value>
        </SelectTriggerContent>

        {!noArrow && (
          <IconContainer data-cy="icon-container">
            <ArrowDownIcon data-cy="arrow-down" />
          </IconContainer>
        )}
      </SelectTrigger>
      <SelectPrimitive.Portal data-cy="select-portal">
        <SelectContent
          $overwriteHeight={overwritePortalHeight}
          $zIndex={zIndex}
          data-cy="select-content-container"
          onKeyDown={onKeyDown ?? onKeyPress}
          style={customContentStyle}
          side={side}
          sideOffset={sideOffset}
          avoidCollisions={avoidCollisions}
          align={align}
          position={position}
          sticky={sticky}
          onWheel={(e) => e.stopPropagation()}
          collisionPadding={collisionPadding}
        >
          {children}
        </SelectContent>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
};

export interface IBaseSelectItemTextProps
  extends SelectPrimitive.SelectItemTextProps {}

export const BaseSelectItemText = forwardRef<
  HTMLDivElement,
  IBaseSelectItemTextProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseSelectItemText({ ...props }, ref): JSX.Element {
  return <SelectPrimitive.ItemText ref={ref} {...props} />;
});

export interface IBaseSelectArrowUpProps
  extends SelectPrimitive.SelectScrollUpButtonProps {}

export const BaseSelectArrowUp = forwardRef<
  HTMLDivElement,
  IBaseSelectArrowUpProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseSelectArrowUp({ ...props }, ref): JSX.Element {
  return (
    <ArrowUpContainer ref={ref} {...props}>
      <ArrowUp />
    </ArrowUpContainer>
  );
});

export interface IBaseSelectArrowDownProps
  extends SelectPrimitive.SelectScrollDownButtonProps {}

export const BaseSelectArrowDown = forwardRef<
  HTMLDivElement,
  IBaseSelectArrowDownProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseSelectArrowDown({ ...props }, ref): JSX.Element {
  return (
    <ArrowDownContainer ref={ref} {...props}>
      <ArrowDown />
    </ArrowDownContainer>
  );
});

export interface IBaseSelectIndicatorProps
  extends SelectPrimitive.SelectItemIndicatorProps {}

export const BaseSelectIndicator = forwardRef<
  HTMLSpanElement,
  IBaseSelectIndicatorProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseSelectIndicator({ children, ...props }, ref): JSX.Element {
  return (
    <IndicatorContainer ref={ref} {...props}>
      {children ?? <SelectIndicatorIcon />}
    </IndicatorContainer>
  );
});

export interface IBaseSelectIndicatorGlyphProps extends SVGProps<SVGSVGElement> {
  'data-cy'?: string;
}

export const BaseSelectIndicatorGlyph = ({
  'data-cy': dataCy = 'base-select-indicator-glyph',
  ...props
}: IBaseSelectIndicatorGlyphProps): JSX.Element => (
  <SelectIndicatorIcon data-cy={dataCy} {...props} />
);

export interface IBaseSelectViewportProps
  extends SelectPrimitive.SelectViewportProps {}

export const BaseSelectViewport = forwardRef<
  HTMLDivElement,
  IBaseSelectViewportProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseSelectViewport({ ...props }, ref): JSX.Element {
  return <CustomViewport ref={ref} {...props} />;
});

export interface IBaseSelectItemProps extends SelectPrimitive.SelectItemProps {}

export const BaseSelectItem = forwardRef<
  HTMLDivElement,
  IBaseSelectItemProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseSelectItem({ ...props }, ref): JSX.Element {
  return <CustomItem ref={ref} {...props} />;
});
